import { getDefaultZIndex, Group } from '@mantine/core';
import { DateInput, DateInputProps, DateTimePicker } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { parseDate } from 'src/utils';

const DATE_FORMAT = 'MM/DD/YYYY';
const DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm a';

// DateInput and DateTimePicker are rooted on different native elements
// (input vs. button), so React's DOM event handlers (onFocus, onKeyDown,
// onPointerDown, etc.) are typed against different EventTarget shapes and
// aren't structurally interchangeable between the two, even though every
// other prop (data/decoration/calendar settings) is. No current caller
// forwards a native handler through DatePicker, so the whole handler
// surface is omitted from the shared prop type rather than allowed
// through for one branch and silently mistyped for the other.
type NativeDomEventHandlerKey = keyof React.DOMAttributes<Element>;

export interface IDatePickerProps
  extends Omit<
    DateInputProps,
    | 'value'
    | 'onChange'
    | 'defaultValue'
    | 'valueFormat'
    | 'type'
    | NativeDomEventHandlerKey
    // Mantine's styles-api props (`vars`/`classNames`/`styles`) accept a
    // callback typed against the component's own Factory payload (its full
    // props + element ref), so a callback written for DateInputProps isn't
    // reusable for DateTimePicker's differently-shaped payload either.
    | 'vars'
    | 'classNames'
    | 'styles'
  > {
  value?: Date | string | null;
  onChange: (value: Date | null) => void;
  /** Renders a DateTimePicker instead of a date-only DateInput. */
  enableTime?: boolean;
  /** Adds a "Now" button that sets the value to the current date/time. */
  showNow?: boolean;
}

/** @registry Unified date input: DateTimePicker when `enableTime`, otherwise a date-only DateInput with a calendar icon; optional "Now" button sets the current date/time. Extends DateInputProps so callers can pass error/id/name/placeholder through for Formik/Yup validation display. */
export const DatePicker: React.FC<IDatePickerProps> = ({
  value,
  onChange,
  enableTime = false,
  showNow = false,
  required = false,
  disabled = false,
  placeholder,
  ...rest
}) => {
  const dateValue = parseDate(value);

  const handleNowClick = () => {
    onChange(new Date());
  };

  const input = enableTime ? (
    <DateTimePicker
      {...rest}
      value={dateValue}
      onChange={onChange}
      placeholder={placeholder ?? DATE_TIME_FORMAT}
      valueFormat={DATE_TIME_FORMAT}
      clearable
      disabled={disabled}
      required={required}
      popoverProps={{ zIndex: getDefaultZIndex('max') }}
      style={{ flexGrow: 1, ...rest.style }}
    />
  ) : (
    <DateInput
      {...rest}
      value={dateValue}
      onChange={onChange}
      rightSection={<IconCalendar size={16} />}
      placeholder={placeholder ?? DATE_FORMAT}
      valueFormat={DATE_FORMAT}
      clearable
      disabled={disabled}
      required={required}
      style={{ flexGrow: 1, ...rest.style }}
    />
  );

  if (!showNow) return input;

  return (
    <Group align="flex-end" gap="xs">
      {input}
      <BasicButton
        w="80px"
        size="sm"
        variant="light"
        disabled={disabled}
        onClick={handleNowClick}
      >
        Now
      </BasicButton>
    </Group>
  );
};
